import { LOD, Vector3, type Camera, type ShaderMaterial } from 'three';
import type { DayWindow } from '../../../time/daylight';
import type { Animated, BuildContext, FeatureOutput, WorldFeature } from '../../types';
import { CROWD } from './crowdConfig';
import { createCrowdDrawing, type CrowdDrawing } from './crowdDrawing';
import { createCrowdSim, type CrowdSim } from './crowdSim';
import { planResidents, type Residents } from './residents';
import { restoreWalkNet } from './walkNet';
import { crowdDoorActivity } from './crowdDoorways';

export const CROWD_PACE = {
  /** Routes are found for this long a frame until all are known, a few frames after the start. */
  prepareMs: 3,
  /** The city's clock steps by the second; the crowd follows it only when it has leapt. */
  clockSlackS: 1.25,
} as const;

/* The renderer hands a LOD the camera it is about to draw with (mesh/nearDetailLod.ts). */
class CrowdRoot extends LOD {
  constructor(private readonly select: (camera: Camera) => void) {
    super();
    this.name = 'crowd';
  }

  override update(camera: Camera): void {
    this.select(camera);
  }
}

function painterAmount(material: ShaderMaterial): number {
  const amount: unknown = material.uniforms['uPainterAmount']?.value;
  return typeof amount === 'number' ? amount : 0;
}

/** The day the crowd keeps: from a little after sunrise to a little before sunset, in minutes. */
export function crowdDay(day: DayWindow): { readonly open: number; readonly close: number } {
  return {
    open: Math.max(CROWD.earliestMin, day.sunrise + CROWD.dawnMarginMin),
    close: Math.min(CROWD.latestMin, day.sunset - CROWD.duskMarginMin),
  };
}

/**
 * The crowd is raised after the city is built, over its first frames: planning ten thousand days
 * and shaping their figures takes some 60 ms, and the city's build has none to spare
 * (docs/PLAN.md §10). The city opens without its residents, and they are out a moment later.
 * Without the citizens' file there is nobody to draw, and no crowd.
 */
export function buildCrowd(ctx: BuildContext): FeatureOutput {
  const baked = ctx.lifePlan?.crowd,
    citizens = ctx.citizens;
  if (!baked || baked.doors.length === 0 || !citizens)
    return { objects: [], obstacles: [], animated: [], counts: { residents: 0 } };
  const material = ctx.materials.painted();
  ctx.materials.setCitizenPoses(citizens.poses, citizens.bones.length);
  let residents: Residents | undefined,
    sim: CrowdSim | undefined,
    drawing: CrowdDrawing | undefined;
  const stages = [
    (): void => {
      const net = restoreWalkNet(baked.net);
      residents = planResidents(baked, net, 1538);
      sim = createCrowdSim(baked, net, residents, (p) => ctx.site.queries.groundHeightAt(p));
    },
    (): void => {
      drawing = createCrowdDrawing(material, citizens, residents!);
      root.add(...drawing.meshes);
    },
  ];
  let stage = 0;

  let sceneSeconds = 0,
    clockLead = NaN,
    open = 0,
    close = 0,
    drawnAt = '';
  const eye = new Vector3(),
    forward = new Vector3();

  const select = (camera: Camera): void => {
    if (!drawing || !sim) return;
    eye.setFromMatrixPosition(camera.matrixWorld);
    forward.set(0, 0, -1).transformDirection(camera.matrixWorld);
    const sinceOpen = sceneSeconds + clockLead - open * 60;
    // A frame is drawn in several passes with one camera; the crowd is chosen once for them all.
    const key = `${String(sinceOpen)}|${eye.toArray().join()}|${forward.toArray().join()}`;
    if (key === drawnAt) return;
    drawnAt = key;
    const shown = painterAmount(material) < 0.5 && Number.isFinite(sinceOpen);
    const frame = shown ? sim.frame(sinceOpen, (close - open) * 60) : null;
    if (ctx.crowdDoors) crowdDoorActivity(ctx.crowdDoors, frame, residents!, baked);
    const { drawn, near } = drawing.draw(frame, eye, forward, sceneSeconds);
    root.userData['crowdDrawn'] = drawn;
    root.userData['crowdDrawnNear'] = near;
  };
  const root = new CrowdRoot(select);

  const animated: Animated = {
    id: 'crowd',
    update(timeSeconds) {
      sceneSeconds = timeSeconds;
      if (stage < stages.length) stages[stage++]!();
      else sim!.prepare(CROWD_PACE.prepareMs);
    },
    setTimeOfDay(minutes, _activity, day) {
      if (day) ({ open, close } = crowdDay(day));
      const lead = minutes * 60 - sceneSeconds;
      if (!(Math.abs(lead - clockLead) <= CROWD_PACE.clockSlackS)) clockLead = lead;
    },
  };
  return {
    objects: [root],
    obstacles: [],
    animated: [animated],
    counts: {
      residents: CROWD.residents,
      crowdDoors: baked.doors.length / 5,
      crowdPlaces: baked.places.length / 3,
      crowdNetPoints: baked.net.nodes.length / 3,
    },
  };
}

export const crowdFeature: WorldFeature = {
  id: 'crowd',
  label: 'Residents',
  weight: 1,
  build: buildCrowd,
};
